<template>
  <main class="bg-gray-200 min-h-screen">
    <header class="">
      <div
        class="border-b border-gray-300 px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8"
      >
        <div class="flex-1 min-w-0">
          <h1 class="text-lg font-medium leading-6 text-gray-900 sm:truncate">
            {{ $t('settings_title') }}
          </h1>
        </div>
      </div>
      <sqr-progress v-show="loading" />
      <sqr-error-banner :error="loadError" @retry="patientInit(true)" />
    </header>

    <section class="mt-8" v-if="account && !loadError">
      <div class="max-w-7xl px-4">
        <div class="grid lg:grid-cols-3 gap-4">
          <div class="bg-white p-4 rounded shadow">
            <h2 class="font-medium text-gray-700 mb-6">
              {{ $t('settings_display_title') }}
            </h2>
            <div class="mb-3">
              <sqr-input-checkbox
                class="mb-1"
                :label="$t('settings_preview')"
                :placeholder="$t('settings_preview_placeholder')"
                :value="account.preview"
                @change="fieldSet({ field: 'preview', value: $event })"
              />
              <div class="text-xs text-gray-700">
                {{ $t('settings_preview_help') }}
              </div>
            </div>
            <div class="mb-3">
              <sqr-input-checkbox
                class="mb-1"
                :label="$t('settings_use_quantity')"
                :placeholder="$t('settings_use_quantity_placeholder')"
                :value="account.useQuantity"
                @change="fieldSet({ field: 'useQuantity', value: $event })"
              />
              <div class="text-xs text-gray-700">
                {{ $t('settings_use_quantity_help') }}
              </div>
            </div>
          </div>
          <div class="bg-white p-4 rounded shadow">
            <h2 class="font-medium text-gray-700 mb-6">
              {{ $t('settings_invoice_title') }}
            </h2>
            <sqr-input-select
              :label="$t('settings_issuer')"
              :value="account.issuerId"
              @change="fieldSet({ field: 'issuerId', value: $event })"
              :options="issuersWithLabel"
            />
            <div class="text-right">
              <router-link
                :to="{ name: 'issuers', params: { aid } }"
                class="text-sm text-blue-600 hover:text-blue-500 underline"
              >
                {{ $t('settings_issuers_edit') }}
              </router-link>
            </div>
            <div class="mb-3">
              <sqr-input-checkbox
                :placeholder="$t('settings_no_provider')"
                :value="account.noProvider"
                @change="fieldSet({ field: 'noProvider', value: $event })"
              />
            </div>
            <sqr-input-select
              :label="$t('settings_provider')"
              :value="account.providerId"
              @change="fieldSet({ field: 'providerId', value: $event })"
              :options="providersWithLabel"
              :disabled="account.noProvider"
            />
            <div class="text-right">
              <router-link
                :to="{ name: 'providers', params: { aid } }"
                class="text-sm text-blue-600 hover:text-blue-500 underline"
              >
                {{ $t('settings_providers_edit') }}
              </router-link>
            </div>
            <div class="mb-3">
              <sqr-input-percent
                class="w-24"
                :label="$t('settings_vat_rate')"
                :value="account.vatRate"
                @change="fieldSet({ field: 'vatRate', value: $event })"
              />
            </div>
          </div>
          <div class="bg-white p-4 rounded shadow">
            <h2 class="font-medium text-gray-700 mb-6">
              {{ $t('settings_patient_title') }}
            </h2>
            <sqr-input-select
              class="mb-3"
              :label="$t('settings_patient_canton')"
              :value="account.canton"
              @change="fieldSet({ field: 'canton', value: $event })"
              :options="cantons"
              is-fullwidth
            />
            <sqr-input-select
              class="mb-3"
              :label="$t('settings_patient_law')"
              :value="account.law"
              @change="fieldSet({ field: 'law', value: $event })"
              :options="laws"
              is-fullwidth
            />
            <sqr-input-radio
              class="mb-3"
              :label="$t('settings_patient_refund_type')"
              :value="account.refundType"
              @change="fieldSet({ field: 'refundType', value: $event })"
              :options="refundTypes"
            />
            <!-- after 10 patients if all the same put the default in place. -->
          </div>
          <div class="bg-white p-4 rounded shadow">
            <h2 class="font-medium text-gray-700 mb-3">
              {{ $t('settings_models') }}
            </h2>
            <p class="text-gray-800">
              <i18n path="settings_models_text" tag="label" for="tos">
                <router-link
                  :to="{ name: 'models', params: { aid } }"
                  class="text-blue-600 hover:text-blue-500 underline"
                  >{{ $t('settings_models_link') }}</router-link
                >
              </i18n>
            </p>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SqrInputSelect from '@/components/SqrInputSelect';
import SqrInputCheckbox from '@/components/SqrInputCheckbox';
import SqrInputRadio from '@/components/SqrInputRadio';
import SqrInputPercent from '@/components/SqrInputPercent';
import SqrProgress from '@/components/SqrProgress';
import SqrErrorBanner from '@/components/SqrErrorBanner';

export default {
  name: 'Settings',
  components: {
    SqrInputSelect,
    SqrInputCheckbox,
    SqrInputPercent,
    SqrInputRadio,
    SqrProgress,
    SqrErrorBanner
  },
  props: {
    aid: String
  },
  computed: {
    ...mapGetters('account', [
      'account',
      'issuers',
      'providers',
      'loading',
      'loadError',
      'save',
      'saveError'
    ]),
    ...mapGetters('options', ['cantons', 'refundTypes', 'laws']),
    providersWithLabel() {
      return this.providers?.map(issuer => ({ ...issuer, label: issuer.name }));
    },
    issuersWithLabel() {
      return this.issuers?.map(issuer => ({ ...issuer, label: issuer.name }));
    }
  },
  mounted() {
    this.$a().logEvent('settings');
  },
  methods: {
    ...mapActions('account', ['fieldSet'])
  }
};
</script>
